import { BarChart3, HardDrive, MessageSquare, ShieldCheck, type LucideIcon } from 'lucide-react';

const AGION_HOST_DOMAINS = [
  'agion.ai',
  'agion.dev',
  'agion.cloud',
  'agion.fi',
] as const;

export type AgionAppId = 'admin' | 'control' | 'app' | 'analytics';

export interface AppSwitcherApp {
  id: AgionAppId | string;
  name: string;
  href: string;
  description?: string;
  icon?: LucideIcon;
}

interface LocationLike {
  protocol: string;
  hostname: string;
  port: string;
}

const AGION_APP_IDS: readonly AgionAppId[] = ['admin', 'control', 'app', 'analytics'];
const LOCALHOST_PORTS: Record<AgionAppId, string> = {
  admin: '4001',
  app: '4002',
  control: '4003',
  analytics: '4004',
};

const HOST_APP_ALIASES: Record<string, AgionAppId> = {
  admin: 'admin',
  control: 'control',
  app: 'app',
  chat: 'app',
  analytics: 'analytics',
};

const KNOWN_SERVICE_SUBDOMAINS = new Set([
  ...Object.keys(HOST_APP_ALIASES),
  'api',
  'auth',
  'login',
]);

const APP_ICONS: Record<AgionAppId, LucideIcon> = {
  admin: ShieldCheck,
  control: HardDrive,
  app: MessageSquare,
  analytics: BarChart3,
};

export function getCurrentAgionAppId(locationLike: LocationLike = window.location): AgionAppId | null {
  const hostname = locationLike.hostname.toLowerCase();
  const firstLabel = hostname.split('.')[0] ?? '';
  const hostApp = HOST_APP_ALIASES[firstLabel];

  if (hostApp) return hostApp;

  if (isLocalHost(hostname)) {
    for (const appId of AGION_APP_IDS) {
      if (LOCALHOST_PORTS[appId] === locationLike.port) return appId;
    }
  }

  return null;
}

export function buildAgionAppUrl(appId: AgionAppId, locationLike: LocationLike = window.location): string {
  const protocol = normalizeProtocol(locationLike.protocol);
  const hostname = locationLike.hostname.toLowerCase();

  if (isLocalHost(hostname)) {
    return formatOrigin(protocol, hostname, LOCALHOST_PORTS[appId]);
  }

  const labels = hostname.split('.').filter(Boolean);
  if (labels.length >= 2) {
    const firstLabel = labels[0] ?? '';

    if (KNOWN_SERVICE_SUBDOMAINS.has(firstLabel)) {
      labels[0] = appId;
      return formatOrigin(protocol, labels.join('.'), locationLike.port);
    }

    if (isAgionHost(hostname)) {
      return formatOrigin(protocol, `${appId}.${hostname}`, locationLike.port);
    }

    if (labels.length > 2) {
      labels[0] = appId;
      return formatOrigin(protocol, labels.join('.'), locationLike.port);
    }
  }

  return formatOrigin(protocol, hostname, locationLike.port);
}

export function createAgionAppSwitcherApps(
  labels: Record<AgionAppId, string>,
  locationLike: LocationLike = window.location
): AppSwitcherApp[] {
  return AGION_APP_IDS.map((appId) => ({
    id: appId,
    name: labels[appId],
    href: buildAgionAppUrl(appId, locationLike),
    icon: APP_ICONS[appId],
  }));
}

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function isAgionHost(hostname: string): boolean {
  return AGION_HOST_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

function normalizeProtocol(protocol: string): string {
  return protocol.endsWith(':') ? protocol : `${protocol}:`;
}

function formatOrigin(protocol: string, hostname: string, port: string): string {
  const host = hostname.includes(':') && !hostname.startsWith('[') ? `[${hostname}]` : hostname;
  return `${protocol}//${host}${port ? `:${port}` : ''}/`;
}
